/* @flow */

import type { Product, ProductCardProduct, ProductCategory } from "shop-state/types";
import type { Option } from "@crossroads/ui-components";

import React, { useState, useEffect, useMemo } from "react";
import { useData } from "crustate/react";
import { useLocation } from "react-router";
import { Helmet } from "react-helmet-async";
import { useAnalytics } from "context/analytics";
import { useTranslate } from "@awardit/react-use-translate";
import { useGetProductMeta } from "helpers/get-meta";
import useFormat from "helpers/use-format";
import useBrowserDimensions from "helpers/use-browser-dimensions";
import { QuoteData } from "data";
import { Link } from "react-router-dom";
import Wrapper from "components/Wrapper";
import ProductList from "components/ProductList";
import Breadcrumbs from "components/Breadcrumbs";
import {
  Accordion,
  ProductViewMedia,
  AutoExpand,
  AddToCart,
  productDefaults,
  inStock,
  getAttributesConfigurable,
  getSelectedConfigurable,
  getPrice,
} from "@crossroads/ui-components";
import Button from "components/Button";
import useBreadcrumbLinks from "helpers/use-breadcrumb-links";
import ChevronIcon from "icons/chevron-big.svg";
import CloseIcon from "icons/close2.svg";

import styles from "./styles.scss";

type ProductViewProps = {
  product: Product,
};

type HintProductViewProps = {
  product: ProductCardProduct,
};

type StructuredCategory = {
  name: string,
  url: string,
  children: Array<{
    name: string,
    url: string,
  }>,
};

const Price = ({ product }: { product: Product }): React$Node => {
  const t = useTranslate();
  const { formatPrice } = useFormat();
  const { price: { incVat: price }, attributes: { msrp } } = product;
  const discount = Math.floor((msrp - price) / msrp * 100);

  return (
    <div className={styles.priceWrapper}>
      <p className={styles.price}>{formatPrice(price)}</p>
      {discount > 0 && discount < 100 && <p className={styles.msrp}>{`${t("PRODUCT.MSRP")} ${formatPrice(msrp)}`}</p>}
    </div>

  );
};

const CustomCloseIcon = (): React$Node => {
  const t = useTranslate();

  return <CloseIcon aria-label={t("ICONS.CROSS")} className={styles.customCloseIcon} />;
};

const AddToCartBtn = ({
  loading = false, outOfStock,
}: { loading?: boolean, outOfStock: boolean,
}): React$Node => {
  const t = useTranslate();
  const quoteState = useData(QuoteData);
  const addingToCart = quoteState.state === "ADDING_ITEM";

  return (
    <Button
      className={styles.addToCartButton}
      variant="primary"
      loading={addingToCart || loading}
      disabled={outOfStock}
      type="submit"
    >
      {outOfStock ? t("PRODUCT.OUT_OF_STOCK") : t("PRODUCT.ADD_TO_CART")}
    </Button>
  );
};

const mapProductCategories = (
  categories: $ReadOnlyArray<ProductCategory>
): Array<StructuredCategory> => {
  const structuredCategories: Array<StructuredCategory> = [];

  for (const category of categories) {
    if (category.parent) {
      let parent = structuredCategories.find(c => c.url === category.parent?.url);

      if (parent) {
        parent.children.push({
          name: category.name,
          url: category.url,
        });
      }
      else {
        parent = {
          name: category.parent?.name || "",
          url: category.parent?.url || "",
          children: [{
            name: category.name,
            url: category.url,
          }],
        };

        structuredCategories.push(parent);
      }
    }
    else {
      structuredCategories.push({
        name: category.name,
        url: category.url,
        children: [],
      });
    }
  }

  return structuredCategories;
};

const ProductView = ({ product }: ProductViewProps): React$Node => {
  const t = useTranslate();
  const location = useLocation();
  const analytics = useAnalytics();
  const configAttributes = product.type === "configurable" ? getAttributesConfigurable(product) : {};
  const [selected, setSelected] = useState<Option>(productDefaults(product));
  const selectedItem = getSelectedConfigurable(selected, configAttributes);
  const brand = product.attributes.manufacturer;
  const { manufacturer } = product.attrDescriptions;
  const meta = useGetProductMeta(product, t);
  const outOfStock = !inStock(product, selected);
  const { width: browserWidth } = useBrowserDimensions();
  const { smallImage, largeImage } = product.attributes;
  const categories = useMemo(() => mapProductCategories(product.categories), [product]);
  const breadcrumbLinks = useBreadcrumbLinks({ fallbackCategories: categories });

  const [currentImage, setCurrentImage] = useState({
    smallImage: smallImage ? { x1: smallImage.x1, x2: smallImage.x2 } : null,
    largeImage: largeImage ? { x1: largeImage.x1, x2: largeImage.x2 } : null,
  });

  const numberOfProductsToShow = () => {
    if (browserWidth < Number.parseInt(styles.small, 10)) {
      return 2;
    }

    return 3;
  };

  useEffect(() => {
    analytics.registerProductDetailsView({
      sku: product.sku,
      name: product.name,
      price: product.price,
      qty: 1,
      attributes: {
        manufacturer: product.attributes.manufacturer,
      },
      categories: product.categories,
    },
    product.price.incVat,
    location.state?.list,
    location.state?.position);
  }, []);

  const onAdd = p => {
    if (selectedItem) {
      const incVat = getPrice(product, selected, "incVat");
      const exVat = getPrice(product, selected, "exVat");

      analytics.registerModifyCart({
        ...selectedItem.product,
        qty: 1,
        sku: product.sku,
        price: { incVat, exVat, vat: incVat - exVat },
        categories: p.categories,
      }, 1, incVat);
    }
    else {
      analytics.registerModifyCart({
        ...p,
        qty: 1,
      }, 1, p.price.incVat);
    }
  };

  // Change image when selecting option
  useEffect(() => {
    if (selectedItem) {
      const a = selectedItem.product.attributes;

      if (a) {
        setCurrentImage({
          smallImage: a.smallImage ? { x1: a.smallImage.x1, x2: a.smallImage.x2 } : null,
          largeImage: a.largeImage ? { x1: a.largeImage.x1, x2: a.largeImage.x2 } : null,
        });
      }
    }
  }, [selected]);

  useEffect(() => {
    setCurrentImage({
      smallImage,
      largeImage,
    });
  }, [smallImage, largeImage]);

  return (
    <Wrapper className={styles.wrapper}>
      <Helmet
        title={meta.title}
        meta={meta.data}
        link={meta.link}
      />

      <Breadcrumbs
        className={styles.breadcrumbs}
        links={breadcrumbLinks}
        current={product.name}
      />

      <header className={styles.header__outer}>
        <div className={styles.top}>
          <h1 className={styles.name}>{product.name}</h1>
          <div className={styles.brand}>
            {brand}
          </div>
        </div>
      </header>
      <div className={styles.split}>
        <div className={styles.left}>
          <header className={styles.header__inner}>
            <div className={styles.top}>
              <h1 className={styles.name}>{product.name}</h1>
              <div className={styles.brand}>{brand}</div>
              <Price product={product} />
            </div>
          </header>
          <section className={styles.addToCart}>

            <AddToCart
              product={product}
              selected={selected}
              setSelected={setSelected}
              qty={1}
              onAdd={onAdd}
            >
              <AddToCartBtn outOfStock={outOfStock} />
            </AddToCart>
          </section>

          <Accordion
            id="productDescription"
            title={t("PRODUCT.DESCRIPTION")}
            ToggleIcon={ChevronIcon}
          >
            <div
              dangerouslySetInnerHTML={{ __html: product.attributes.description }}
              className={styles.description}
            />
          </Accordion>

          {manufacturer && manufacturer.icon && manufacturer.description &&
            <Accordion
              id="aboutBrand"
              title={t("PRODUCT.ABOUT_BRAND", { brand: manufacturer.title })}
              ToggleIcon={ChevronIcon}
            >
              <div className={styles.manufacturer}>
                <div className={styles.logo}>
                  <img
                    src={manufacturer ? manufacturer.icon : ""}
                    alt={`Logo ${brand}`}
                  />
                </div>
                <p>{manufacturer ? manufacturer.description : ""}</p>
              </div>
            </Accordion>
          }
        </div>

        <div className={styles.right}>
          <ProductViewMedia
            CloseIcon={CustomCloseIcon}
            alt={product.name}
            setCurrentImage={setCurrentImage}
            currentImage={currentImage}
            gallery={product.gallery}
            location={location}
            galleryLocation="bottom"
          />
        </div>
      </div>

      <AutoExpand>
        &nbsp;
        <div className={styles.lists}>
          <div className={styles.relatedList}>
            {product.relatedProducts.items.length > 0 &&
              <ProductList
                heading={t("PRODUCT.RELATED_PRODUCTS")}
                products={product.relatedProducts.items.slice(0, numberOfProductsToShow())}
                productsPerRow={numberOfProductsToShow()}
              />
            }
          </div>

          <div className={styles.historyList}>
            {product.crossSellProducts.items.length > 0 &&
              <ProductList
                heading={t("PRODUCT.CROSS_SELL_PRODUCTS")}
                products={product.crossSellProducts.items.slice(0, numberOfProductsToShow())}
                productsPerRow={numberOfProductsToShow()}
              />
            }
          </div>
        </div>
      </AutoExpand>
    </Wrapper>
  );
};

export const HintProductView = ({ product }: HintProductViewProps): React$Node => {
  const location = useLocation();
  const brand = product.attributes.manufacturer;
  const image = product.attributes.largeImage ||
  product.attributes.smallImage ||
  location.state?.image || { x1: "", x2: "" };
  const breadcrumbLinks = useBreadcrumbLinks();

  return (
    <Wrapper className={styles.hintWrapper}>
      <Breadcrumbs
        className={styles.breadcrumbs}
        links={breadcrumbLinks}
        current={product.name}
      />

      <header className={styles.header__outer}>
        <div className={styles.brand}>
          {brand}
        </div>
        <h1 className={styles.name}>{product.name}</h1>
      </header>

      <div className={styles.split}>

        <div className={styles.left}>
          <header className={styles.header__inner}>
            <div className={styles.top}>
              <Link
                to={{
                  pathname: `/brand/${encodeURIComponent(brand)}`,
                  state: { hint: { category: { name: brand } } },
                }}
                className={styles.brand}
              >
                {brand}
              </Link>
              <h1 className={styles.name}>{product.name}</h1>
            </div>
          </header>

          <div className={styles.dummyContent}>
            <div className={styles.top} />
            <div className={styles.middle} />
            <div className={styles.bottom} />
          </div>
        </div>

        <div className={styles.right}>
          <ProductViewMedia
            alt={product.name}
            setCurrentImage={() => {}}
            currentImage={{
              largeImage: image,
              smallImage: image,
            }}
            gallery={[]}
            location={location}
            galleryLocation="bottom"
          />
        </div>
      </div>
    </Wrapper>
  );
};

export default ProductView;
